//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import Indicator from '@components/atoms/indicator/Indicator';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { IDirections } from '@models/IDirections';
import { TrackingIdPrefix } from '@models/TrackingId';
import { Mixins } from '@theme/mixins';
import { getFuzzySearch } from '@utils/getFuzzySearch';
import { withTracking } from '@utils/withTracking';

/**
 * `DropdownMenu` is a flexible and accessible dropdown menu,
 * capable of containing a variety of content types including actions, links, and nested components.
 *
 * The `DropdownMenu` component can be enhanced with search functionality, provided by the `DropdownMenu.Search` sub-component,
 * enabling dynamic filtering of dropdown content based on user input. This search component is generic, allowing for the
 * searching of items of any type as long as they conform to the expected item structure.
 *
 * Note: When using custom components as triggers or within the menu, it's crucial to apply the `forwardRef` pattern
 * to maintain functionality. Failure to do so may result in unresponsive or incorrectly functioning dropdowns, leading
 * to potential confusion about the component's behavior.
 * 
 * For example, if you want to use our buttons from the atoms folder we can consider using them like:
 * <Button buttonStyles={DefaultButtonStyles}>Label</Button>
 *
 * @example
 * This example showcases the `DropdownMenu` with various features such as dynamic search, submenus, checkboxes for selection,
 * and radio groups for single-choice options. It demonstrates how to configure a dropdown menu with a search bar for filtering
 * items, use submenus for organizing items, and include selectable items like checkboxes and radio buttons for user interaction.
 *
 * <DropdownMenu>
 *   <DropdownMenu.Trigger asChild disabled={isDisabled}>
        <Button buttonStyles={DefaultButtonStyles}>{trigger}</Button>
 *   </DropdownMenu.Trigger>
 *   <DropdownContent side={side} align={align}>
 *      <DropdownMenu.SearchBar
 *        handleClear={() => ({})}
 *        onChange={() => ({})}
 *        placeholder={'Search'}
 *        value={'term'}
 *        trackingIdPrefix='story'
 *        visuallyHiddenLabel='story'
 *      />
 *     <DropdownMenu.Item onClick={handleClick}>Item 1</DropdownMenu.Item>
 *     <DropdownMenu.Sub>
 *       <DropdownMenu.SubTrigger>SubTrigger 1</DropdownMenu.SubTrigger>
 *       <DropdownMenu.SubContent>
 *           <DropdownMenu.Item>
 *             SubItem 1
 *           </DropdownMenu.Item>
 *           <DropdownMenu.Item>
 *             SubItem 2
 *           </DropdownMenu.Item>
 *           <DropdownMenu.Item>
 *             SubItem 3
 *           </DropdownMenu.Item>
 *       </DropdownMenu.SubContent>
 *     </DropdownMenu.Sub>
 *
 *     <DropdownMenu.Label>Option</DropdownMenu.Label>
 *     <DropdownMenu.RadioGroup value={option} onValueChange={setOption}>
 *       <DropdownMenu.RadioItem value="option1">
 *         Option 1
 *         <DropdownMenu.ItemIndicator>
 *           <DropdownMenu.IndicatorIcon />
 *         </DropdownMenu.ItemIndicator>
 *       </DropdownMenu.RadioItem>
 *       <DropdownMenu.RadioItem value="option2">
 *         Option 2
 *         <DropdownMenu.ItemIndicator>
 *           <DropdownMenu.IndicatorIcon />
 *         </DropdownMenu.ItemIndicator>
 *       </DropdownMenu.RadioItem>
 *     </DropdownMenu.RadioGroup>
 *
 *     <DropdownMenu.CheckboxItem
 *       checked={checked}
 *       onCheckedChange={onCheckedChange}
 *      >
 *         Checkbox item
 *       <DropdownMenu.ItemIndicator>
 *         <DropdownMenu.SelectedIcon />
 *       </DropdownMenu.ItemIndicator>
 *     </DropdownMenu.CheckboxItem>
 *   </DropdownContent>
 * </DropdownMenu>
 */
export const DropdownMenu = ({
  children,
  ...props
}: RadixDropdownMenu.DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenuContext.Provider value={{ isOpen }}>
      <DropdownMenuRoot onOpenChange={setIsOpen} open={isOpen} {...props}>
        {children}
      </DropdownMenuRoot>
    </DropdownMenuContext.Provider>
  );
};

const DropdownMenuRoot = styled(RadixDropdownMenu.Root)`
  ${Mixins.Text.UiCopy}
`;

interface DropDownMenuSearchProps<T> {
  className?: string;
  defaultSearchValue?: string;
  handleClear?: () => void;
  handleSearch: (searchTerm: string, filteredItems: T[]) => void;
  itemsToSearch: T[];
  searchBarConfig: {
    placeholder: string;
    visuallyHiddenLabel: string;
    threshold?: number;
    trackingIdPrefix: TrackingIdPrefix;
  };
  searchKeys: string[];
}

const DropdownMenuContext = createContext<{ isOpen: boolean }>({
  isOpen: false,
});

const DropdownMenuSearch = <T,>({
  className,
  defaultSearchValue,
  handleClear,
  handleSearch,
  itemsToSearch,
  searchBarConfig,
  searchKeys,
}: DropDownMenuSearchProps<T>) => {
  const [searchValue, setSearchValue] = useState(defaultSearchValue ?? '');
  const { isOpen } = useContext(DropdownMenuContext);

  const resetSearch = useCallback(() => {
    setSearchValue('');
    if (handleClear) {
      handleClear();
    }
    handleSearch('', itemsToSearch);
  }, [handleClear, handleSearch, itemsToSearch]);

  // When dropdown closes the search value is cleared because DropdownMenu.Content is unmounted
  // this results in a prefiltered state on the next open, but without a search value
  // This effect ensures that the search results are cleared to reflect the clearing of the search value
  useEffect(() => {
    if (!isOpen) {
      resetSearch();
    }
  }, [isOpen, resetSearch]);

  const handleChange = (searchTerm: string) => {
    setSearchValue(searchTerm);
    if (searchTerm === '') {
      handleSearch(searchTerm, itemsToSearch);
    } else {
      const filteredItems = getFuzzySearch({
        itemsToSearch,
        searchKeys,
        searchTerm,
        threshold: searchBarConfig.threshold,
      });
      handleSearch(searchTerm, filteredItems);
    }
  };

  // Radix DropdownMenu will automatically focus on the first option that starts with the inserted key, but in this case, we want to handle the search.
  return (
    <SearchBarContainer onKeyDown={(event) => event.stopPropagation()}>
      <SearchBar
        {...searchBarConfig}
        className={className}
        handleClear={handleClear ? resetSearch : undefined}
        onChange={handleChange}
        value={searchValue}
      />
    </SearchBarContainer>
  );
};

const SearchBarContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[2]} calc(${theme.spacings[2]} + ${theme.spacings[1]});
  `,
);

const uiCopyStyle = () => css`
  ${Mixins.Text.UiCopy}
`;

const DropdownMenuTrigger = withTracking(
  styled(RadixDropdownMenu.Trigger)(uiCopyStyle),
);

interface DropdownContentProps extends RadixDropdownMenu.MenuContentProps {
  children: ReactNode;
  className?: string;
  container?: HTMLElement;
}

const DropdownMenuContent = ({
  children,
  className,
  container,
  sideOffset = 8,
  ...props
}: DropdownContentProps) => {
  return (
    <RadixDropdownMenu.Portal container={container}>
      <StyledDropdownContent
        className={classNames('unison-component', className)}
        sideOffset={sideOffset}
        {...props}
      >
        {children}
      </StyledDropdownContent>
    </RadixDropdownMenu.Portal>
  );
};

const baseStyledContentWrapperStyle = `
  width: ${theme.smallMeasure};
  padding: ${theme.spacings[2]} ${theme.spacings[0]};
  box-shadow: ${theme.boxShadows[3]};
  border: ${theme.borderWidths[1]} solid ${theme.colors.gray3};
  background-color: ${theme.colors.white};
  z-index: ${theme.zindex.popover};
`;

const baseStyledItemWrapperStyle = `
  ${Mixins.Text.UiCopy}
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.spacings[2]};
  position: relative;
  height: 100%;
  width: 100%;
  min-height: calc(${theme.spacings[5]} + ${theme.spacings[2]});
  outline: none;
`;

const styledContentWrapper = () => css`
  ${baseStyledContentWrapperStyle}
`;

const styledSubContentWrapper = () => css`
  ${baseStyledContentWrapperStyle}
`;

const StyledDropdownContent = styled(RadixDropdownMenu.Content)(
  styledContentWrapper,
);

const baseStyledLabelWrapper = () => css`
  ${baseStyledItemWrapperStyle}
  padding: ${theme.spacings[0]}
    calc(${theme.spacings[2]} + ${theme.spacings[1]});
`;

const baseStyledItemWrapper = () => css`
  ${baseStyledItemWrapperStyle}
  padding: ${theme.spacings[0]}
    calc(${theme.spacings[2]} + ${theme.spacings[1]});
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.gray0};
  }

  &:focus {
    background-color: ${theme.colors.gray0};
  }
`;

const DropdownMenuItem = withTracking(
  styled(RadixDropdownMenu.Item)(
    ({ theme }) => css`
      ${baseStyledItemWrapper};

      &[data-disabled] {
        color: ${theme.colors.gray4};
        background-color: transparent;
        border-color: transparent;
      }
    `,
  ),
);

const DropdownMenuRadioItem = withTracking(
  styled(RadixDropdownMenu.RadioItem)(baseStyledItemWrapper),
);

const DropdownMenuCheckboxItem = withTracking(
  styled(RadixDropdownMenu.CheckboxItem)(baseStyledItemWrapper),
);

const DropdownMenuLabel = styled(RadixDropdownMenu.Label)(
  baseStyledLabelWrapper,
);

const DropdownMenuSubTrigger = withTracking(
  styled(RadixDropdownMenu.SubTrigger)(
    ({ theme }) => css`
      ${baseStyledItemWrapper};

      &[data-disabled] {
        color: ${theme.colors.gray4};
        background-color: transparent;
        border-color: transparent;
      }
    `,
  ),
);

interface DropdownSubContentProps
  extends RadixDropdownMenu.MenuSubContentProps {
  children: ReactNode;
  className?: string;
}

const DropdownMenuSubContent = ({
  children,
  className,
  ...props
}: DropdownSubContentProps) => {
  return (
    <RadixDropdownMenu.Portal>
      <StyledDropdownSubContent
        className={classNames('unison-component', className)}
        {...props}
      >
        {children}
      </StyledDropdownSubContent>
    </RadixDropdownMenu.Portal>
  );
};

const DropdownMenuChildNavIcon = styled(Icon).attrs({
  name: 'chevron-right',
})(
  ({ theme }) => `
    padding-left: ${theme.spacings[1]};
  `,
);

const DropdownMenuSelectedIcon = styled(Icon).attrs({
  name: 'checkmark',
})(
  ({ theme }) => css`
    margin-left: auto;
    color: ${theme.colors.green7};
  `,
);

const StyledDropdownSubContent = styled(RadixDropdownMenu.SubContent)(
  styledSubContentWrapper,
);

const DropdownMenuItemIndicator = styled(RadixDropdownMenu.ItemIndicator)(
  () => `
    ${Mixins.Text.UiCopy}
    display: flex;
    align-items: center;
  `,
);

interface DropdownMenuIndicatorIconProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  showIf?: boolean;
  offset?: IDirections<number>;
}

const DropdownMenuIndicatorIcon = (props: DropdownMenuIndicatorIconProps) => (
  <Indicator showIf={true} {...props} />
);

const DropdownMenuSub = styled(RadixDropdownMenu.Sub)`
  ${Mixins.Text.UiCopy}
`;

const DropdownMenuGroup = styled(RadixDropdownMenu.Group)`
  ${Mixins.Text.UiCopy}
`;

const DropdownMenuRadioGroup = styled(RadixDropdownMenu.RadioGroup)`
  ${Mixins.Text.UiCopy}
`;

DropdownMenu.CheckboxItem = DropdownMenuCheckboxItem;
DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.PlainContent = StyledDropdownContent;
DropdownMenu.Group = DropdownMenuGroup;
DropdownMenu.IndicatorIcon = DropdownMenuIndicatorIcon;
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.ItemIndicator = DropdownMenuItemIndicator;
DropdownMenu.Label = DropdownMenuLabel;
DropdownMenu.NavIcon = DropdownMenuChildNavIcon;
DropdownMenu.RadioGroup = DropdownMenuRadioGroup;
DropdownMenu.RadioItem = DropdownMenuRadioItem;
DropdownMenu.Search = DropdownMenuSearch;
DropdownMenu.SelectedIcon = DropdownMenuSelectedIcon;
DropdownMenu.Sub = DropdownMenuSub;
DropdownMenu.SubContent = DropdownMenuSubContent;
DropdownMenu.SubTrigger = DropdownMenuSubTrigger;
DropdownMenu.Trigger = DropdownMenuTrigger;
